import { useEffect, useState } from "react";
import { Repeat2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TooltipButton } from "@/components/common/Tooltips";
import { api, apiError } from "@/lib/api";

const ORIG_ROLES = ["ejecutivo_credito", "analista_credito", "admin_entidad"];

export function ReasignarCliente({ cliente, onDone }) {
  const [open, setOpen] = useState(false);
  const [cobradores, setCobradores] = useState([]);
  const [originadores, setOriginadores] = useState([]);
  const [cobradorId, setCobradorId] = useState("");
  const [origId, setOrigId] = useState("");
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCobradorId(cliente?.cobrador_id || "");
    setOrigId(cliente?.originado_por || "");
    setMotivo("");
    api.get("/usuarios/cobradores").then((r) => setCobradores(r.data || [])).catch(() => {});
    api.get("/usuarios").then((r) => setOriginadores((r.data || []).filter((u) => ORIG_ROLES.includes(u.role)))).catch(() => {});
  }, [open, cliente]);

  const submit = async () => {
    if (!motivo.trim()) { toast.error("Captura el motivo de la reasignación."); return; }
    setBusy(true);
    try {
      await api.post(`/clientes/${cliente.id}/reasignar`, {
        motivo: motivo.trim(),
        cobrador_id: cobradorId || "",
        originado_por: origId || "",
      });
      toast.success("Reasignación aplicada y registrada en el historial.");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <TooltipButton tooltip="Cambiar el responsable de cartera de este cliente"
        size="sm" variant="outline" data-testid="c360-reasignar-btn" onClick={() => setOpen(true)}>
        <Repeat2 className="mr-1.5 h-4 w-4" /> Reasignar
      </TooltipButton>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="reasignar-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reasignar cliente</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Responsable de cartera (cobranza)</Label>
              <Select value={cobradorId || "__none"} onValueChange={(v) => setCobradorId(v === "__none" ? "" : v)}>
                <SelectTrigger data-testid="reasignar-cobrador"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Sin asignar (bandeja de pendientes)</SelectItem>
                  {cobradores.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Originador</Label>
              <Select value={origId || "__none"} onValueChange={(v) => setOrigId(v === "__none" ? "" : v)}>
                <SelectTrigger data-testid="reasignar-originador"><SelectValue placeholder="Sin originador" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Sin originador</SelectItem>
                  {originadores.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Motivo *</Label>
              <Textarea data-testid="reasignar-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}
                placeholder="Justifica la reasignación (queda registrada en el historial)." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancelar</Button>
            <Button data-testid="reasignar-submit" onClick={submit} disabled={busy} className="bg-accent hover:bg-forest-700">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
